// src/pages/Dashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "../components/PageHeader";
import { abrirRelatorio } from "pages/utils/abrirRelatorio";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useTheme } from "@mui/material/styles";
import Draggable from "react-draggable";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import DashboardLazyAccordion from "pages/components/DashboardLazyAccordion";
import MateriaisRiscoDashboard from "pages/components/MateriaisRiscoDashboard";
import ObjetivosEstadoDashboard from "pages/components/ObjetivosEstadoDashboard";
import IncentivosVendedorDashboard from "pages/components/IncentivosVendedorDashboard";
import VendasMensaisVendedoresDashboard from "pages/components/VendasMensaisVendedoresDashboard";

// ========== Config ==========
const FILTERS_KEY = "tbl:dashboard:saldosvendedores:filters";
const KEEP_KEY = "tbl:dashboard:saldosvendedores:keep";
const ENDPOINT = "Dashboard/SaldosVendedores";

const MemoizedDefaultLineChart = React.memo(DefaultLineChart);

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

// ========== Filtro simples (Playbook) ==========
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// ========== Helpers ==========
const fmt = new Intl.NumberFormat("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => (v == null || isNaN(v) ? 0 : Number(v));

const getMaxMesesFechadosAno = () => {
  const mesAtual = new Date().getMonth() + 1; // 1-12
  return mesAtual === 1 ? 12 : mesAtual - 1;
};

const normalizarMesesFechadosAno = (valor) => {
  const max = getMaxMesesFechadosAno();
  const n = parseInt(valor, 10);

  if (Number.isNaN(n)) return 1;
  if (n < 1) return 1;
  if (n > max) return max;

  return n;
};

const getAnosAnaliseMesesFechadosAno = () => {
  const hoje = new Date();
  const mesAtual = hoje.getMonth() + 1;

  if (mesAtual === 1) {
    return {
      anoAtualAnalise: hoje.getFullYear() - 1,
      anoAnteriorAnalise: hoje.getFullYear() - 2,
    };
  }

  return {
    anoAtualAnalise: hoje.getFullYear(),
    anoAnteriorAnalise: hoje.getFullYear() - 1,
  };
};

const getMesLabel = (mes) => {
  const meses = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];

  if (!mes || mes < 1) return "";
  if (mes > 12) return "Dez";

  return meses[mes - 1];
};

// =================== Componente ===================
export default function Dashboard() {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const theme = useTheme();

  const ultimoRefreshAutoRef = useRef(0);

  const [kpi, setKpi] = useState({ sum_91a120: 0, sum_121a150: 0, sum_151mais: 0 });

  const [topDialogOpen, setTopDialogOpen] = useState(false);
  const [topDialogTitle, setTopDialogTitle] = useState("");
  const [topDialogRows, setTopDialogRows] = useState([]);
  const [topDialogLoading, setTopDialogLoading] = useState(false);

  const [topDialogHeaders, setTopDialogHeaders] = useState([
    "#",
    "Nome",
    "Total € (12m)",
    "Total € (12m ant.)",
    "Dif €",
    "Dif %",
  ]);

  const [topParceiros, setTopParceiros] = useState([]);
  const [topParceirosTotal, setTopParceirosTotal] = useState(0);
  const [topClientes, setTopClientes] = useState([]);
  const [topClientesTotal, setTopClientesTotal] = useState(0);
  const [topSubiram, setTopSubiram] = useState([]);
  const [topSubiramTotal, setTopSubiramTotal] = useState(0);
  const [topDesceram, setTopDesceram] = useState([]);
  const [topDesceramTotal, setTopDesceramTotal] = useState(0);

  const [loadingKpis12Meses, setLoadingKpis12Meses] = useState(false);
  const [kpis12MesesCarregados, setKpis12MesesCarregados] = useState(false);

  const [mesesFechadosAno, setMesesFechadosAno] = useState(() => getMaxMesesFechadosAno());

  const [topParceirosAno, setTopParceirosAno] = useState([]);
  const [topParceirosAnoTotal, setTopParceirosAnoTotal] = useState(0);
  const [topClientesAno, setTopClientesAno] = useState([]);
  const [topClientesAnoTotal, setTopClientesAnoTotal] = useState(0);
  const [topSubiramAno, setTopSubiramAno] = useState([]);
  const [topSubiramAnoTotal, setTopSubiramAnoTotal] = useState(0);
  const [topDesceramAno, setTopDesceramAno] = useState([]);
  const [topDesceramAnoTotal, setTopDesceramAnoTotal] = useState(0);

  const [loadingKpisAno, setLoadingKpisAno] = useState(false);
  const [kpisAnoCarregados, setKpisAnoCarregados] = useState(false);

  const [incentivosRefreshToken, setIncentivosRefreshToken] = useState(0);
  const [loadingIncentivos, setLoadingIncentivos] = useState(false);
  const [incentivosCarregados, setIncentivosCarregados] = useState(false);
  const [incentivosVazios, setIncentivosVazios] = useState(false);

  const [objetivosRefreshToken, setObjetivosRefreshToken] = useState(0);
  const [loadingObjetivos, setLoadingObjetivos] = useState(false);
  const [objetivosCarregados, setObjetivosCarregados] = useState(false);

  const [materiaisRiscoRefreshToken, setMateriaisRiscoRefreshToken] = useState(0);
  const [loadingMateriaisRisco, setLoadingMateriaisRisco] = useState(false);
  const [materiaisRiscoCarregados, setMateriaisRiscoCarregados] = useState(false);
  const [materiaisRiscoVazios, setMateriaisRiscoVazios] = useState(false);

  const [vendasVendedoresRefreshToken, setVendasVendedoresRefreshToken] = useState(0);
  const [loadingVendasVendedores, setLoadingVendasVendedores] = useState(false);
  const [vendasVendedoresCarregadas, setVendasVendedoresCarregadas] = useState(false);
  const [vendasVendedoresVazias, setVendasVendedoresVazias] = useState(false);

  const [loadingDebitosSaldos, setLoadingDebitosSaldos] = useState(false);
  const [debitosSaldosCarregados, setDebitosSaldosCarregados] = useState(false);

  async function carregarTopDesceramKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

    const [data, err] = await handleApi(api.post("Dashboard/SelClientesMaisDesceram12Meses", body));
    if (err || !Array.isArray(data)) return;

    setTopDesceram(data.slice(0, 3));

    // Aqui faz sentido mostrar a "perda" total (soma das difs, valor negativo)
    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopDesceramTotal(totalDif20);
  }

  async function carregarTopSubiramKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

    const [data, err] = await handleApi(api.post("Dashboard/SelClientesMaisSubiram12Meses", body));
    if (err || !Array.isArray(data)) return;

    setTopSubiram(data.slice(0, 3));

    // Para este KPI, o valor principal faz mais sentido ser a soma das DIFs (ganho)
    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopSubiramTotal(totalDif20);
  }

  async function carregarTopParceirosKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

    const [data, err] = await handleApi(api.post("Dashboard/SelTopParceiros12Meses", body));

    if (err || !Array.isArray(data)) return;

    // Top 3 para o pódio
    setTopParceiros(data.slice(0, 3));

    // Total agregado do Top 20 (período atual)
    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);

    setTopParceirosTotal(total20);
  }

  async function carregarTopClientesKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

    const [data, err] = await handleApi(api.post("Dashboard/SelTopClientes12Meses", body));

    if (err || !Array.isArray(data)) return;

    setTopClientes(data.slice(0, 3));

    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);
    setTopClientesTotal(total20);
  }

  async function carregarTopParceirosAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? { meses: mesesFechadosAno }
      : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

    const [data, err] = await handleApi(
      api.post("Dashboard/SelTopParceirosAnoMesesFechados", body)
    );

    if (err || !Array.isArray(data)) return;

    setTopParceirosAno(data.slice(0, 3));

    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);
    setTopParceirosAnoTotal(total20);
  }

  async function carregarTopClientesAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? { meses: mesesFechadosAno }
      : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

    const [data, err] = await handleApi(api.post("Dashboard/SelTopClientesAnoMesesFechados", body));

    if (err || !Array.isArray(data)) return;

    setTopClientesAno(data.slice(0, 3));

    const total20 = data.reduce((sum, r) => sum + Number(r?.total_atual || 0), 0);
    setTopClientesAnoTotal(total20);
  }

  async function carregarTopSubiramAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? { meses: mesesFechadosAno }
      : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

    const [data, err] = await handleApi(
      api.post("Dashboard/SelClientesMaisSubiramAnoMesesFechados", body)
    );

    if (err || !Array.isArray(data)) return;

    setTopSubiramAno(data.slice(0, 3));

    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopSubiramAnoTotal(totalDif20);
  }

  async function carregarTopDesceramAnoKpi() {
    const podeVerGeral =
      temFuncao("Administração") ||
      temFuncao("Informática") ||
      temFuncao("Tesouraria") ||
      temFuncao("Gestão Objetivos");

    const body = podeVerGeral
      ? { meses: mesesFechadosAno }
      : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

    const [data, err] = await handleApi(
      api.post("Dashboard/SelClientesMaisDesceramAnoMesesFechados", body)
    );

    if (err || !Array.isArray(data)) return;

    setTopDesceramAno(data.slice(0, 3));

    const totalDif20 = data.reduce((sum, r) => sum + Number(r?.dif || 0), 0);
    setTopDesceramAnoTotal(totalDif20);
  }

  function atualizarIncentivos() {
    if (loadingIncentivos) return;

    setLoadingIncentivos(true);
    setIncentivosRefreshToken((prev) => prev + 1);
  }

  function atualizarObjetivos() {
    if (loadingObjetivos) return;

    setLoadingObjetivos(true);
    setObjetivosRefreshToken((prev) => prev + 1);
  }

  function atualizarMateriaisRisco() {
    if (loadingMateriaisRisco) return;

    setLoadingMateriaisRisco(true);
    setMateriaisRiscoRefreshToken((prev) => prev + 1);
  }

  function atualizarVendasVendedores() {
    if (loadingVendasVendedores) return;

    setLoadingVendasVendedores(true);
    setVendasVendedoresRefreshToken((prev) => prev + 1);
  }

  async function atualizarDebitosSaldos() {
    if (loadingDebitosSaldos) return;

    setLoadingDebitosSaldos(true);

    try {
      await Promise.all([carregarKpi(), carregar()]);

      setDebitosSaldosCarregados(true);
    } finally {
      setLoadingDebitosSaldos(false);
    }
  }

  async function atualizarKpis12Meses() {
    if (loadingKpis12Meses) return;

    setLoadingKpis12Meses(true);

    try {
      await Promise.all([
        carregarTopParceirosKpi(),
        carregarTopClientesKpi(),
        carregarTopSubiramKpi(),
        carregarTopDesceramKpi(),
      ]);

      setKpis12MesesCarregados(true);
    } finally {
      setLoadingKpis12Meses(false);
    }
  }

  async function atualizarKpisAno() {
    if (loadingKpisAno) return;

    setLoadingKpisAno(true);

    try {
      await Promise.all([
        carregarTopParceirosAnoKpi(),
        carregarTopClientesAnoKpi(),
        carregarTopSubiramAnoKpi(),
        carregarTopDesceramAnoKpi(),
      ]);

      setKpisAnoCarregados(true);
    } finally {
      setLoadingKpisAno(false);
    }
  }

  async function abrirTop20Desceram() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

      setTopDialogTitle("Top 20 Clientes que mais desceram (12m vs 12m anteriores)");
      setTopDialogHeaders(["#", "Nome", "Total € (12m)", "Total € (12m ant.)", "Dif €", "Dif %"]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisDesceram12Meses", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Desceram).", "error");
    }
  }

  async function abrirTop20Subiram() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

      setTopDialogTitle("Top 20 Clientes que mais subiram (12m vs 12m anteriores)");
      setTopDialogHeaders(["#", "Nome", "Total € (12m)", "Total € (12m ant.)", "Dif €", "Dif %"]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisSubiram12Meses", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Subiram).", "error");
    }
  }

  async function abrirTop20Parceiros() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

      setTopDialogTitle("Top 20 Parceiros (12m vs 12m anteriores)");
      setTopDialogHeaders(["#", "Nome", "Total € (12m)", "Total € (12m ant.)", "Dif €", "Dif %"]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(api.post("Dashboard/SelTopParceiros12Meses", body));
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Parceiros.", "error");
    }
  }

  async function abrirTop20Clientes() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral ? {} : { refvendedor: userData?.refvendedor || null };

      setTopDialogTitle("Top 20 Clientes (12m vs 12m anteriores)");
      setTopDialogHeaders(["#", "Nome", "Total € (12m)", "Total € (12m ant.)", "Dif €", "Dif %"]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(api.post("Dashboard/SelTopClientes12Meses", body));
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Clientes.", "error");
    }
  }

  async function abrirTop20ParceirosAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? { meses: mesesFechadosAno }
        : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

      setTopDialogTitle(
        `Top 20 Parceiros (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelTopParceirosAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Parceiros.", "error");
    }
  }

  async function abrirTop20ClientesAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? { meses: mesesFechadosAno }
        : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

      setTopDialogTitle(
        `Top 20 Clientes (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelTopClientesAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 Clientes.", "error");
    }
  }

  async function abrirTop20SubiramAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? { meses: mesesFechadosAno }
        : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

      setTopDialogTitle(
        `Top 20 Clientes que mais subiram (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisSubiramAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Subiram).", "error");
    }
  }

  async function abrirTop20DesceramAno() {
    try {
      const podeVerGeral =
        temFuncao("Administração") ||
        temFuncao("Informática") ||
        temFuncao("Tesouraria") ||
        temFuncao("Gestão Objetivos");

      const body = podeVerGeral
        ? { meses: mesesFechadosAno }
        : { refvendedor: userData?.refvendedor || null, meses: mesesFechadosAno };

      setTopDialogTitle(
        `Top 20 Clientes que mais desceram (${anoAtualAnalise} Jan-${mesLabel} vs ${anoAnteriorAnalise} Jan-${mesLabel})`
      );
      setTopDialogHeaders([
        "#",
        "Nome",
        `Total € (${anoAtualAnalise} Jan-${mesLabel})`,
        `Total € (${anoAnteriorAnalise} Jan-${mesLabel})`,
        "Dif €",
        "Dif %",
      ]);
      setTopDialogRows([]);
      setTopDialogOpen(true);
      setTopDialogLoading(true);

      const [data, err] = await handleApi(
        api.post("Dashboard/SelClientesMaisDesceramAnoMesesFechados", body)
      );
      setTopDialogLoading(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setTopDialogRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setTopDialogLoading(false);
      showAlert(e?.message || "Erro ao carregar Top 20 (Desceram).", "error");
    }
  }

  // Perfis
  const isAdminLike = useMemo(
    () => temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria"),
    [temFuncao]
  );
  const isComercial = useMemo(() => temFuncao("Comercial"), [temFuncao]);
  const podeVerTabela = isAdminLike || isComercial;

  // ref do vendedor autenticado
  const refUser = useMemo(() => userData?.refvendedor?.toString?.() || "", [userData]);
  const podeVerKpis = isAdminLike || (isComercial && !!refUser);

  const podeVerRuturas = isAdminLike;

  // ---------- Estado da tabela ----------
  const [loading, setLoading] = useState(true);
  const [rowsData, setRowsData] = useState([]);
  const [erro, setErro] = useState("");

  // === vendas (para o gráfico) ===
  const [vendas3Anos, setVendas3Anos] = useState([]);
  const [loadingVendas, setLoadingVendas] = useState(false);

  // === última sincronização de faturas de vendas ===
  const [ultimaSyncFaturasVendas, setUltimaSyncFaturasVendas] = useState(null);

  // persistência "Manter filtros"
  const initKeep = (() => {
    try {
      const v = localStorage.getItem(KEEP_KEY);
      return v === null ? true : JSON.parse(v);
    } catch {
      return true;
    }
  })();

  const [keepFilters, setKeepFilters] = useState(initKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  const { anoAtualAnalise, anoAnteriorAnalise } = getAnosAnaliseMesesFechadosAno();

  const mesLabel = getMesLabel(mesesFechadosAno);

  const colorMap = {
    warning: theme.palette.warning.main,
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  };

  const vendasChart = useMemo(() => {
    if (!Array.isArray(vendas3Anos) || vendas3Anos.length === 0) return null;

    const anoAtual = new Date().getFullYear();
    const anos = [anoAtual, anoAtual - 1, anoAtual - 2];

    const labelsMeses = [
      "Jan",
      "Fev",
      "Mar",
      "Abr",
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
    ];

    const porAno = {};
    const totaisAno = {};
    anos.forEach((a) => {
      porAno[a] = new Array(12).fill(0);
      totaisAno[a] = 0;
    });

    for (const r of vendas3Anos) {
      const ano = Number(r.ano);
      const mes = Number(r.mes); // 1-12
      const valor = Number(r.total ?? 0);

      if (!anos.includes(ano)) continue;
      const idxMes = mes - 1;
      if (idxMes < 0 || idxMes > 11) continue;

      porAno[ano][idxMes] += valor;
      totaisAno[ano] += valor;
    }

    // Esconder meses FUTUROS do ano atual (como no MenuSearch)
    const mesAtual = new Date().getMonth(); // 0-11

    for (let m = mesAtual + 1; m < 12; m += 1) {
      if (porAno[anoAtual]) {
        porAno[anoAtual][m] = null;
      }
    }

    const tipo = isAdminLike ? "Geral" : "Comercial";
    const cores = ["warning", "primary", "secondary"];

    const datasets = anos.map((ano, idx) => ({
      // label: `${ano} – ${tipo} (${fmt.format(totaisAno[ano])} €)`,
      label: `${ano}`,
      data: porAno[ano],
      tension: 0.4,
      pointRadius: 3,
      borderWidth: 3,
      color: cores[idx] || "info",
    }));

    return {
      labels: labelsMeses,
      datasets,
      totaisAno,
      tipo,
    };
  }, [vendas3Anos, isAdminLike]);

  const vendasChartData = useMemo(() => {
    if (!vendasChart) return null;

    return {
      labels: vendasChart.labels,
      datasets: vendasChart.datasets,
    };
  }, [vendasChart]);

  // ======= Carregar dados (só tenta se pode ver) =======
  const carregar = async () => {
    if (!podeVerTabela) return;
    setLoading(true);
    const [data, err] = await handleApi(api.post(ENDPOINT, {}));
    if (err) {
      setErro(err);
      showAlert(err, "error");
      setLoading(false);
      return;
    }
    setRowsData(Array.isArray(data) ? data : []);
    setErro("");
    setLoading(false);
  };

  useEffect(() => {
    const refreshAuto = () => {
      const agora = Date.now();

      // Evita chamadas duplicadas quando focus e visibilitychange disparam quase ao mesmo tempo
      if (agora - ultimoRefreshAutoRef.current < 1500) return;

      ultimoRefreshAutoRef.current = agora;
      refreshDashboardCompleto();
    };

    const handleFocus = () => {
      refreshAuto();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshAuto();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [podeVerTabela, podeVerKpis, refUser, mesesFechadosAno]);

  // Tenta ler JSON de um blob (quando a API devolve erro com envelope)
  async function tryParseJsonBlob(blob) {
    try {
      const text = await blob.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  async function gerarRelatorio(tipo, par_refvendedor) {
    setLoading(true);
    try {
      const res = await api.post(
        "Relatorios/ObterRptSaldosVendedor",
        { tipo, par_refvendedor },
        { responseType: "blob" }
      );

      setLoading(false);

      const contentType = res.headers["content-type"] || "";
      const blob = new Blob([res.data], { type: contentType });

      if (contentType.includes("application/json")) {
        const j = await tryParseJsonBlob(blob);
        const url = j?.data?.url;
        if (url) {
          // iOS → mesma aba; restantes → nova aba
          abrirRelatorio(url);
          return;
        }
        const msg = j?.message || "Erro ao gerar relatório.";
        showAlert(msg, "error");
        return;
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Saldos_${par_refvendedor}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      setLoading(false);
      const msg = e?.message || "Erro de comunicação ao gerar relatório.";
      showAlert(msg, "error");
    }
  }

  async function abrirMais90Pdf() {
    try {
      setLoading(true);

      let res = null;
      if (isComercial) {
        res = await api.post(
          "Relatorios/ObterRptMais90DiasPorVendedor",
          { par_refvendedor: refUser },
          { responseType: "blob" }
        );
      } else {
        res = await api.post("Relatorios/ObterRptMais90Dias", {}, { responseType: "blob" });
      }

      const ct = res.headers["content-type"] || "";
      const blob = new Blob([res.data], { type: ct });
      if (ct.includes("application/json")) {
        const txt = await blob.text();
        let j = null;
        try {
          j = JSON.parse(txt);
        } catch {}
        const url = j?.data?.url;
        if (url) {
          // Mesma regra: iOS na mesma aba, resto nova aba
          abrirRelatorio(url);
          return;
        }
        showAlert(j?.message || "Erro ao gerar relatório.", "error");
        return;
      }
    } finally {
      setLoading(false);
    }
  }

  const carregarKpi = async () => {
    if (!podeVerKpis) return;
    setLoading(true);

    let resp;
    if (isAdminLike) {
      resp = await handleApi(api.post("Dashboard/SumClientesEmAtraso", {}));
    } else {
      resp = await handleApi(
        api.post("Dashboard/SumClientesEmAtrasoPorVendedor", { refvendedor: refUser })
      );
    }

    const [data, err] = resp;
    setLoading(false);
    if (err) return;

    setKpi({
      sum_91a120: Number(data?.sum_91a120 ?? 0),
      sum_121a150: Number(data?.sum_121a150 ?? 0),
      sum_151mais: Number(data?.sum_151mais ?? 0),
    });
  };

  const carregarUltimaSyncFaturasVendas = async () => {
    const [data, err] = await handleApi(api.post("Dashboard/UltimaSincronizacaoFaturasVendas", {}));

    if (err) {
      setUltimaSyncFaturasVendas(null);
      return;
    }

    setUltimaSyncFaturasVendas(data?.atualizado_em || null);
  };

  const carregarVendas = async () => {
    // Apenas utilizadores autorizados podem ver o dashboard geral
    const podeVerGeral =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    setLoadingVendas(true);

    // Se for Comercial e NÃO fizer parte dos perfis acima → filtro obrigatório
    const body = podeVerGeral
      ? {} // geral
      : { refvendedor: userData?.refvendedor || null };

    const [data, err] = await handleApi(api.post("Dashboard/SelVendas3Anos", body));

    setLoadingVendas(false);

    if (err) return showAlert(err, "error");

    setVendas3Anos(Array.isArray(data) ? data : []);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // AdminLike: pode carregar sem refUser
    const isAdminLike =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    // Se NÃO for adminLike, precisamos mesmo de refUser (refvendedor) antes de chamar endpoints
    if (!isAdminLike && !refUser) return;

    carregarVendas();
    carregarUltimaSyncFaturasVendas();
  }, [podeVerKpis, refUser]);

  const refreshDashboardCompleto = () => {
    const adminLikeAtual =
      temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

    if (!adminLikeAtual && !refUser) return;

    if (podeVerKpis) {
      carregarVendas();
      carregarUltimaSyncFaturasVendas();
    }
  };

  // --------- regra única para ver a linha ---------
  const podeVerLinha = (refVendLinha) =>
    isAdminLike || (isComercial && refUser && String(refUser) === String(refVendLinha));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 88,
        minWidth: 72,
        maxWidth: 112,
        Cell: ({ row }) => {
          const refVendedor = row?.original?.refvendedor;
          const habilitado = podeVerLinha(refVendedor);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              <Tooltip title="Abrir PDF">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    disabled={!habilitado}
                    onClick={() => gerarRelatorio("pdf", refVendedor)}
                  >
                    <PictureAsPdfIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Exportar Excel">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    disabled={!habilitado}
                    onClick={() => gerarRelatorio("xlsx", refVendedor)}
                  >
                    <GridOnIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          );
        },
      },

      { Header: "Ref.", accessor: "refvendedor", Filter: TextFilter },
      { Header: "Vendedor", accessor: "vendedor", Filter: TextFilter },
      {
        Header: "Saldo Total",
        accessor: "saldototal",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "A Vencer",
        accessor: "avencer",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "Vencido",
        accessor: "vencidototal",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "1–30",
        accessor: "dias_1a30",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "31–60",
        accessor: "dias_31a60",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "61–90",
        accessor: "dias_61a90",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "91–120",
        accessor: "dias_91a120",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "121–150",
        accessor: "dias_121a150",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
      {
        Header: "151+",
        accessor: "dias_151mais",
        Filter: TextFilter,
        Cell: ({ value }) => fmt.format(num(value)),
      },
    ],
    // DEPS — evita “stale memo” das permissões
    [isAdminLike, isComercial, refUser]
  );

  // ======= Tabela =======
  const dataFiltrada = useMemo(() => {
    if (!podeVerTabela) return [];
    if (isAdminLike) return rowsData;
    if (isComercial && refUser) return rowsData.filter((r) => String(r?.refvendedor) === refUser);
    return [];
  }, [rowsData, podeVerTabela, isAdminLike, isComercial, refUser]);

  const table = useTable(
    {
      columns,
      data: dataFiltrada,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  // gerir persistência de filtros
  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [rowsData]); // eslint-disable-line

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      {ultimaSyncFaturasVendas && (
        <MDBox
          mb={1.5}
          display="flex"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          sx={{ width: "100%" }}
        >
          <MDTypography
            variant="caption"
            color="text"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: "normal",
            }}
          >
            Dados de vendas atualizados em{" "}
            <strong>
              {new Date(ultimaSyncFaturasVendas).toLocaleString("pt-PT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </strong>
          </MDTypography>
        </MDBox>
      )}

      {podeVerKpis && (
        <DashboardLazyAccordion
          title="Vendas dos últimos 3 anos (sem IVA e sem vasilhame)"
          subtitle="Totais anuais indicados na legenda. Valores em euros, sempre sem IVA."
          loading={loadingVendas}
          loaded
          initialExpanded
          showRefreshButton={false}
        >
          {loadingVendas || !vendasChart ? (
            <MDBox py={4} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : (
            <>
              <MDBox mb={1.5}>
                <MemoizedDefaultLineChart chart={vendasChartData} height="16rem" />
              </MDBox>

              <MDBox display="flex" flexWrap="wrap" gap={2} mb={0.5}>
                {vendasChart.datasets.map((ds) => {
                  const ano = ds.label.split("–")[0].trim();
                  const totalAno = vendasChart.totaisAno?.[ano] ?? 0;

                  return (
                    <MDTypography
                      key={ds.label}
                      variant="button"
                      color="text"
                      sx={{
                        fontSize: 12,
                        display: "inline-flex",
                        alignItems: "center",
                        mr: 1.5,
                      }}
                    >
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          marginRight: 6,
                          backgroundColor: colorMap[ds.color] || "#999",
                          display: "inline-block",
                        }}
                      />
                      {ano}
                      <span style={{ marginLeft: 4 }}>
                        ( <strong>{fmt.format(totalAno)} €</strong> )
                      </span>
                    </MDTypography>
                  );
                })}
              </MDBox>

              <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                Tipo de somatório:{" "}
                <strong>
                  {vendasChart.tipo === "Geral"
                    ? "Geral (todos os vendedores)"
                    : "Comercial (apenas o vendedor autenticado)"}
                </strong>
              </MDTypography>
            </>
          )}
        </DashboardLazyAccordion>
      )}

      <DashboardLazyAccordion
        title="Objetivos"
        subtitle="Objetivos ativos ou recentes e respetivo progresso."
        buttonLabel="Atualizar"
        loading={loadingObjetivos}
        loaded={objetivosCarregados}
        onRefresh={atualizarObjetivos}
      >
        <ObjetivosEstadoDashboard
          autoLoad={false}
          refreshToken={objetivosRefreshToken}
          onLoadingChange={setLoadingObjetivos}
          onLoadedChange={setObjetivosCarregados}
        />
      </DashboardLazyAccordion>

      <DashboardLazyAccordion
        title="Incentivos"
        subtitle="Incentivos ativos e posição atual do vendedor."
        buttonLabel="Atualizar"
        loading={loadingIncentivos}
        loaded={incentivosCarregados}
        empty={incentivosVazios}
        emptyMessage="Não foram encontrados incentivos para este utilizador."
        onRefresh={atualizarIncentivos}
      >
        <IncentivosVendedorDashboard
          autoLoad={false}
          refreshToken={incentivosRefreshToken}
          onLoadingChange={setLoadingIncentivos}
          onLoadedChange={setIncentivosCarregados}
          onEmptyChange={setIncentivosVazios}
        />
      </DashboardLazyAccordion>

      <DashboardLazyAccordion
        title="KPIs dos últimos 12 meses"
        subtitle="Compara os últimos 12 meses com os 12 meses anteriores."
        buttonLabel="Atualizar"
        loading={loadingKpis12Meses}
        loaded={kpis12MesesCarregados}
        onRefresh={atualizarKpis12Meses}
      >
        <MDBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          }}
          gap={2}
          sx={{ width: "100%" }}
        >
          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Parceiros}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              {/* Badge (igual ao débitos) */}
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              {/* Conteúdo principal */}
              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Parceiros
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topParceirosTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top 20 (últimos 12 meses)
                </MDTypography>
              </MDBox>

              {/* “…” / detalhe */}
              <Tooltip title="Ver Top 20">
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>

            {/* Rodapé (igual ao débitos: título verde + caption) */}
            {/* <MDBox px={2} pb={2} pt={0.5}>
            <MDTypography
              variant="button"
              sx={{ color: "success.main", fontWeight: "bold", display: "block", mb: 0 }}
            >
              Pódio (Top 3)
            </MDTypography>

            <MDBox mt={0.5}>
              {topParceiros.map((p, idx) => (
                <MDTypography key={p.ref} variant="caption" color="text" sx={{ display: "block" }}>
                  {idx + 1}º {p.nome || p.ref} — <strong>{fmt.format(num(p.total_atual))}</strong>
                </MDTypography>
              ))}

              <MDTypography
                variant="caption"
                color="info"
                sx={{ display: "block", mt: 0.5, fontWeight: "bold" }}
              >
                …
              </MDTypography>
            </MDBox>
          </MDBox> */}
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Clientes}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Clientes
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topClientesTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top 20 (últimos 12 meses)
                </MDTypography>
              </MDBox>

              <Tooltip title="Ver Top 20">
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>

            {/* <MDBox px={2} pb={2} pt={0.5}>
            <MDTypography
              variant="button"
              sx={{ color: "success.main", fontWeight: "bold", display: "block", mb: 0 }}
            >
              Pódio (Top 3)
            </MDTypography>

            <MDBox mt={0.5}>
              {topClientes.map((c, idx) => (
                <MDTypography key={c.ref} variant="caption" color="text" sx={{ display: "block" }}>
                  {idx + 1}º {c.nome || c.ref} — <strong>{fmt.format(num(c.total_atual))}</strong>
                </MDTypography>
              ))}

              <MDTypography
                variant="caption"
                color="info"
                sx={{ display: "block", mt: 0.5, fontWeight: "bold" }}
              >
                …
              </MDTypography>
            </MDBox>
          </MDBox> */}
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Subiram}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingUpIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais subiram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topSubiramTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Ganho Top 20 (dif. 12m vs 12m ant.)
                </MDTypography>
              </MDBox>

              <Tooltip title="Ver Top 20">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20Subiram();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>

            {/* <MDBox px={2} pb={2} pt={0.5}>
            <MDTypography
              variant="button"
              sx={{ color: "success.main", fontWeight: "bold", display: "block" }}
            >
              Pódio (Top 3)
            </MDTypography>

            <MDBox mt={0.5}>
              {topSubiram.map((c, idx) => (
                <MDTypography key={c.ref} variant="caption" color="text" sx={{ display: "block" }}>
                  {idx + 1}º {c.nome || c.ref} — <strong>{fmt.format(num(c.dif))}</strong>
                </MDTypography>
              ))}

              <MDTypography
                variant="caption"
                color="info"
                sx={{ display: "block", mt: 0.5, fontWeight: "bold" }}
              >
                …
              </MDTypography>
            </MDBox>
          </MDBox> */}
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20Desceram}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingDownIcon sx={{ color: "#fff", fontSize: 22 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais desceram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topDesceramTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Perda Top 20 (dif. 12m vs 12m ant.)
                </MDTypography>
              </MDBox>

              <Tooltip title="Ver Top 20">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20Desceram();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLazyAccordion>

      <DashboardLazyAccordion
        title="KPIs anuais por meses fechados"
        subtitle={
          <>
            Compara janeiro até ao último mês terminado escolhido, entre{" "}
            <strong>{anoAtualAnalise}</strong> e <strong>{anoAnteriorAnalise}</strong>.
          </>
        }
        buttonLabel="Atualizar"
        loading={loadingKpisAno}
        loaded={kpisAnoCarregados}
        onRefresh={atualizarKpisAno}
        headerContent={
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <MDTypography variant="button" color="text" sx={{ whiteSpace: "nowrap" }}>
              Meses terminados:
            </MDTypography>

            <input
              type="text"
              value={mesesFechadosAno}
              onChange={(e) => setMesesFechadosAno(normalizarMesesFechadosAno(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 48,
                textAlign: "center",
                padding: "6px 8px",
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: 6,
                fontSize: 13,
                outline: "none",
                background: "#fff",
              }}
            />

            <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap" }}>
              Máximo: <strong>{getMaxMesesFechadosAno()}</strong>
            </MDTypography>
          </MDBox>
        }
      >
        <MDBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          }}
          gap={2}
          sx={{ width: "100%" }}
        >
          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20ParceirosAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Parceiros
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topParceirosAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top 20 ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise} Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title="Ver Top 20">
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20ClientesAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "none",
                  mr: 1.5,
                })}
              >
                <EmojiEventsIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Top Clientes
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topClientesAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Top 20 ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise} Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title="Ver Top 20">
                <LeaderboardIcon color="info" />
              </Tooltip>
            </MDBox>
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20SubiramAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingUpIcon sx={{ color: "#fff", fontSize: 20 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais subiram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topSubiramAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Ganho Top 20 ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise} Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title="Ver Top 20">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20SubiramAno();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>
          </Card>

          <Card sx={{ cursor: "pointer" }} onClick={abrirTop20DesceramAno}>
            <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
              <MDBox
                sx={({ palette, functions }) => ({
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: functions.linearGradient(
                    palette.corbase1.main,
                    palette.corbase1.focus
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1.5,
                })}
              >
                <TrendingDownIcon sx={{ color: "#fff", fontSize: 22 }} />
              </MDBox>

              <MDBox flex={1}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="regular"
                  sx={{ whiteSpace: "normal" }}
                >
                  Clientes que mais desceram
                </MDTypography>

                <MDTypography variant="h5" fontWeight="bold">
                  {fmt.format(num(topDesceramAnoTotal))} €
                </MDTypography>

                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.25 }}>
                  Perda Top 20 ({anoAtualAnalise} Jan-{mesLabel} vs {anoAnteriorAnalise} Jan-
                  {mesLabel})
                </MDTypography>
              </MDBox>

              <Tooltip title="Ver Top 20">
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirTop20DesceramAno();
                    }}
                  >
                    <LeaderboardIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLazyAccordion>

      {!podeVerRuturas ? null : (
        <DashboardLazyAccordion
          title="Materiais com risco de rutura"
          subtitle="Materiais com stock reduzido ou em risco de rutura nos centros selecionados."
          buttonLabel="Atualizar"
          loading={loadingMateriaisRisco}
          loaded={materiaisRiscoCarregados}
          empty={materiaisRiscoVazios}
          emptyMessage="Não foram encontrados materiais com risco de rutura."
          onRefresh={atualizarMateriaisRisco}
        >
          <MateriaisRiscoDashboard
            podeVerRuturas={podeVerRuturas}
            autoLoad={false}
            refreshToken={materiaisRiscoRefreshToken}
            onLoadingChange={setLoadingMateriaisRisco}
            onLoadedChange={setMateriaisRiscoCarregados}
            onEmptyChange={setMateriaisRiscoVazios}
          />
        </DashboardLazyAccordion>
      )}

      <DashboardLazyAccordion
        title="Vendas mensais por vendedor"
        subtitle="Top 5 vendedores dos últimos 13 meses, com possibilidade de análise individual dos restantes."
        buttonLabel="Atualizar"
        loading={loadingVendasVendedores}
        loaded={vendasVendedoresCarregadas}
        empty={vendasVendedoresVazias}
        emptyMessage="Não foram encontrados dados para apresentar."
        onRefresh={atualizarVendasVendedores}
      >
        <VendasMensaisVendedoresDashboard
          autoLoad={false}
          refreshToken={vendasVendedoresRefreshToken}
          onLoadingChange={setLoadingVendasVendedores}
          onLoadedChange={setVendasVendedoresCarregadas}
          onEmptyChange={setVendasVendedoresVazias}
        />
      </DashboardLazyAccordion>

      {!podeVerTabela ? null : (
        <DashboardLazyAccordion
          title="Débitos de clientes e saldos por vendedor"
          subtitle="Resumo dos clientes em atraso e dos saldos por vendedor."
          buttonLabel="Atualizar"
          loading={loadingDebitosSaldos}
          loaded={debitosSaldosCarregados}
          onRefresh={atualizarDebitosSaldos}
        >
          {/* === KPIs de Atrasos === */}
          <MDBox
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))"
            gap={2}
            mb={2}
            sx={{ width: "100%" }}
          >
            {/* 90+ */}
            <Card>
              <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
                <MDBox
                  sx={({ palette, functions }) => ({
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: functions.linearGradient(
                      palette.corbase1.main,
                      palette.corbase1.focus
                    ),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    mr: 1.5,
                  })}
                >
                  <MDTypography component="span" color="white" fontSize={13} lineHeight={1}>
                    90+
                  </MDTypography>
                </MDBox>

                <MDBox flex={1}>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="regular"
                    sx={{ whiteSpace: "normal" }}
                  >
                    De 91 a 120 dias
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {fmt.format(num(kpi.sum_91a120))}
                  </MDTypography>
                </MDBox>

                <Tooltip title="Abrir PDF">
                  <span>
                    <IconButton size="small" color="info" onClick={abrirMais90Pdf}>
                      <PictureAsPdfIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>

              <MDBox px={2} pb={2} pt={1}>
                <MDTypography
                  variant="button"
                  sx={{
                    color: "success.main",
                    fontWeight: "bold",
                    display: "block",
                    mb: 0,
                  }}
                >
                  Débitos de clientes: De 91 a 120 dias
                </MDTypography>

                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", mt: 0.5, whiteSpace: "normal" }}
                >
                  Clique no botão PDF, para mais detalhes.
                </MDTypography>
              </MDBox>
            </Card>

            {/* 120+ */}
            <Card>
              <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
                <MDBox
                  sx={({ palette, functions }) => ({
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: functions.linearGradient(
                      palette.corbase1.main,
                      palette.corbase1.focus
                    ),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    mr: 1.5,
                  })}
                >
                  <MDTypography component="span" color="white" fontSize={13} lineHeight={1}>
                    120+
                  </MDTypography>
                </MDBox>

                <MDBox flex={1}>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="regular"
                    sx={{ whiteSpace: "normal" }}
                  >
                    De 121 a 150 dias
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {fmt.format(num(kpi.sum_121a150))}
                  </MDTypography>
                </MDBox>

                <Tooltip title="Abrir PDF">
                  <span>
                    <IconButton size="small" color="info" onClick={abrirMais90Pdf}>
                      <PictureAsPdfIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>

              <MDBox px={2} pb={2} pt={1}>
                <MDTypography
                  variant="button"
                  sx={{
                    color: "success.main",
                    fontWeight: "bold",
                    display: "block",
                    mb: 0,
                  }}
                >
                  Débitos de clientes: De 121 a 150 dias
                </MDTypography>

                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", mt: 0.5, whiteSpace: "normal" }}
                >
                  Clique no botão PDF, para mais detalhes.
                </MDTypography>
              </MDBox>
            </Card>

            {/* 150+ */}
            <Card>
              <MDBox display="flex" alignItems="center" p={2} pt={1.5}>
                <MDBox
                  sx={({ palette, functions }) => ({
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: functions.linearGradient(
                      palette.corbase1.main,
                      palette.corbase1.focus
                    ),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "none",
                    mr: 1.5,
                  })}
                >
                  <MDTypography component="span" color="white" fontSize={13} lineHeight={1}>
                    150+
                  </MDTypography>
                </MDBox>

                <MDBox flex={1}>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="regular"
                    sx={{ whiteSpace: "normal" }}
                  >
                    Mais de 150 dias
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {fmt.format(num(kpi.sum_151mais))}
                  </MDTypography>
                </MDBox>

                <Tooltip title="Abrir PDF">
                  <span>
                    <IconButton size="small" color="info" onClick={abrirMais90Pdf}>
                      <PictureAsPdfIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </MDBox>

              <MDBox px={2} pb={2} pt={1}>
                <MDTypography
                  variant="button"
                  sx={{
                    color: "success.main",
                    fontWeight: "bold",
                    display: "block",
                    mb: 0,
                  }}
                >
                  Débitos de clientes: Pendentes com mais de 150 dias
                </MDTypography>

                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ display: "block", mt: 0.5, whiteSpace: "normal" }}
                >
                  Clique no botão PDF, para mais detalhes.
                </MDTypography>
              </MDBox>
            </Card>
          </MDBox>

          {/* === Saldos por vendedor === */}
          <Card>
            <MDBox p={2}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                gap={1}
                flexWrap="wrap"
                mb={2}
              >
                <MDTypography variant="h6">Saldos por vendedor</MDTypography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                  sx={{ m: 0 }}
                />
              </MDBox>

              <MDBox sx={{ maxHeight: "40vh", overflow: "auto", borderRadius: 1 }}>
                <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    {headerGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            key={col.id}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              background: "#fff",
                              textAlign: "left",
                              padding: "6px 8px",
                              fontSize: 12,
                              color: "#7b809a",
                              verticalAlign: "bottom",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                              width: col.id === "acoes" ? col.width : undefined,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center" }}>
                              {col.render("Header")}
                              {col.isSorted ? (
                                col.isSortedDesc ? (
                                  <span style={{ marginLeft: 6 }}>▼</span>
                                ) : (
                                  <span style={{ marginLeft: 6 }}>▲</span>
                                )
                              ) : null}
                            </div>

                            {!col.disableFilters && (
                              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                {col.canFilter ? col.render("Filter") : null}
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                      prepareRow(row);

                      return (
                        <tr {...row.getRowProps()} key={row.id}>
                          {row.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id}
                              style={{
                                padding: "8px 10px",
                                borderTop: "1px solid #eee",
                                fontSize: 12,
                                color: "#1a2b4c",
                                whiteSpace: "nowrap",
                                width: cell.column.id === "acoes" ? cell.column.width : undefined,
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}

                    {!rows.length && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{
                            padding: "12px 10px",
                            fontSize: 12,
                            color: "#7b809a",
                          }}
                        >
                          Sem dados para apresentar.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MDBox>

              {erro && (
                <MDBox mt={1}>
                  <MDTypography variant="button" color="error">
                    {erro}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </DashboardLazyAccordion>
      )}

      <Dialog
        open={topDialogOpen}
        onClose={() => setTopDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle id="draggable-dialog-title" sx={{ cursor: "move" }}>
          {topDialogTitle}
        </DialogTitle>

        <DialogContent dividers>
          {topDialogLoading ? (
            <MDBox py={3} display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </MDBox>
          ) : (
            <MDBox sx={{ maxHeight: "60vh", overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {topDialogHeaders.map((h) => (
                      <th
                        key={h}
                        style={{
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          textAlign: "left",
                          padding: "8px 10px",
                          fontSize: 12,
                          color: "#7b809a",
                          borderBottom: "1px solid #eee",
                          zIndex: 1,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topDialogRows.map((r, idx) => (
                    <tr key={`${r?.ref ?? "x"}-${idx}`}>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {idx + 1}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {r?.nome ? `${r.nome} (${r.ref})` : r?.ref ?? ""}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {fmt.format(num(r?.total_atual))}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {fmt.format(num(r?.total_anterior))}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {fmt.format(num(r?.dif))}
                      </td>
                      <td
                        style={{
                          padding: "8px 10px",
                          borderTop: "1px solid #f0f0f0",
                          fontSize: 12,
                        }}
                      >
                        {r?.dif_pct == null ? "—" : `${fmt.format(num(r?.dif_pct))}%`}
                      </td>
                    </tr>
                  ))}

                  {!topDialogRows?.length && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ padding: "12px 10px", fontSize: 12, color: "#7b809a" }}
                      >
                        Sem dados para apresentar.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="info" onClick={() => setTopDialogOpen(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
